import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ShoppingBag, Minus, Plus, Trash2, ExternalLink, Loader2, Tag, Percent } from "lucide-react";
import { useCartStore } from "@/stores/cartStore";
import { formatPrice, calculateDiscountedPrice, DISCOUNT_PERCENTAGE } from "@/lib/shopify";
import { toast } from "sonner";

export const CartDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { 
    items, 
    isLoading, 
    updateQuantity, 
    removeItem, 
    createCheckout,
    getTotalItems,
    getTotalPrice,
  } = useCartStore();
  
  const totalItems = getTotalItems();
  const totalPriceOriginal = getTotalPrice();
  const totalPriceDiscounted = items.reduce((sum, item) => {
    const discountedPrice = parseFloat(calculateDiscountedPrice(item.price.amount));
    return sum + (discountedPrice * item.quantity);
  }, 0);
  const totalSavings = totalPriceOriginal - totalPriceDiscounted;
  const currencyCode = items[0]?.price.currencyCode || 'EUR';

  const handleCheckout = async () => {
    try {
      const checkoutUrl = await createCheckout();
      if (checkoutUrl) {
        // Open in new tab - using location.href as fallback if popup is blocked
        const newWindow = window.open(checkoutUrl, '_blank');
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          // Popup was blocked, use direct navigation
          window.location.href = checkoutUrl;
        }
        setIsOpen(false);
      } else {
        toast.error("Error creating checkout");
      }
    } catch (error) {
      console.error('Checkout failed:', error);
      toast.error("Checkout error");
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <button
          className="p-2 text-muted-foreground hover:text-foreground transition-colors relative"
          aria-label="Cart"
        >
          <ShoppingBag className="h-5 w-5" />
          <span 
            className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-foreground text-background text-[10px] flex items-center justify-center rounded-full"
            aria-hidden="true"
          >
            {totalItems}
          </span>
        </button>
      </SheetTrigger>
      
      <SheetContent className="w-full sm:max-w-lg flex flex-col h-full z-[70]">
        <SheetHeader className="flex-shrink-0">
          <SheetTitle className="font-display text-xl md:text-2xl font-light">Your Cart</SheetTitle>
          <SheetDescription>
            {totalItems === 0 ? "Your cart is empty" : `${totalItems} item${totalItems !== 1 ? 's' : ''} in your cart`}
          </SheetDescription>
        </SheetHeader>
        
        <div className="flex flex-col flex-1 pt-6 min-h-0">
          {items.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <ShoppingBag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Your cart is empty</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Discover our products to start shopping
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Promo code banner */}
              <div className="flex-shrink-0 bg-destructive/10 border border-destructive/20 rounded-lg p-3 mb-4">
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-destructive" />
                  <span className="text-sm font-medium text-destructive">
                    Code NY25 applied: -{DISCOUNT_PERCENTAGE}% off everything
                  </span>
                </div>
              </div>

              {/* Scrollable items area */}
              <div className="flex-1 overflow-y-auto pr-2 min-h-0">
                <div className="space-y-4">
                  {items.map((item) => {
                    const originalPrice = parseFloat(item.price.amount);
                    const discountedPrice = parseFloat(calculateDiscountedPrice(item.price.amount));
                    const itemTotalOriginal = originalPrice * item.quantity;
                    const itemTotalDiscounted = discountedPrice * item.quantity;

                    return (
                      <div key={item.variantId} className="flex gap-3 md:gap-4 p-3 bg-secondary/20 rounded-lg">
                        <div className="w-16 md:w-20 h-16 md:h-20 bg-secondary/30 rounded-md overflow-hidden flex-shrink-0">
                          {item.product.node.images?.edges?.[0]?.node && (
                            <img
                              src={item.product.node.images.edges[0].node.url}
                              alt={item.product.node.title}
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium truncate text-xs md:text-sm">{item.product.node.title}</h4>
                          {item.variantTitle !== 'Default Title' && (
                            <p className="text-xs text-muted-foreground">
                              {item.selectedOptions.map(option => option.value).join(' • ')}
                            </p>
                          )}
                          <div className="mt-1.5 space-y-0.5">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground line-through">
                                {formatPrice(itemTotalOriginal.toFixed(2), item.price.currencyCode)}
                              </span>
                              <span className="text-xs bg-destructive/10 text-destructive px-1 py-0.5 rounded">
                                -{DISCOUNT_PERCENTAGE}%
                              </span>
                            </div>
                            <p className="font-semibold text-destructive text-sm">
                              {formatPrice(itemTotalDiscounted.toFixed(2), item.price.currencyCode)}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => removeItem(item.variantId)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                          
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-6 md:h-7 w-6 md:w-7"
                              onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="w-6 md:w-8 text-center text-xs md:text-sm">{item.quantity}</span>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-6 md:h-7 w-6 md:w-7"
                              onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Fixed checkout section */}
              <div className="flex-shrink-0 space-y-4 pt-6 border-t bg-background">
                {/* Savings highlight */}
                <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Percent className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium text-green-700 dark:text-green-400">Total Savings</span>
                    </div>
                    <span className="text-sm font-bold text-green-600">
                      -{formatPrice(totalSavings.toFixed(2), currencyCode)}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center text-sm text-muted-foreground">
                    <span>Subtotal</span>
                    <span className="line-through">
                      {formatPrice(totalPriceOriginal.toFixed(2), currencyCode)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-base md:text-lg font-medium">Total</span>
                    <span className="text-lg md:text-xl font-bold text-destructive">
                      {formatPrice(totalPriceDiscounted.toFixed(2), currencyCode)}
                    </span>
                  </div>
                </div>
                
                <Button 
                  onClick={handleCheckout}
                  className="w-full" 
                  size="lg"
                  disabled={items.length === 0 || isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Checkout
                    </>
                  )}
                </Button>
                
                <p className="text-xs text-center text-muted-foreground">
                  100% Secure Payment
                </p>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
